import React from 'react';
import { Pencil, User, MapPin, Clock, GraduationCap, BadgeDollarSign, ShieldCheck } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Card, Field, FieldGrid, fmtDate } from './drawerShared';
import { fmtExperience, fmtTri, fmtNotice, fmtQualification, fmtCtc, QUALIFICATIONS } from '../CandidateProfileFields';

const EditAction = ({ canEdit, onEdit, testId }) => canEdit ? (
  <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-xs text-blue-700 hover:bg-blue-50" onClick={onEdit} data-testid={testId}>
    <Pencil className="h-3.5 w-3.5" /> Edit
  </Button>
) : null;

export const PersonalTab = ({ candidate: c, canEdit, onEdit }) => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="tab-panel-personal">
    <Card title="Contact" icon={User} action={<EditAction canEdit={canEdit} onEdit={onEdit} testId="personal-edit-btn" />}>
      <FieldGrid cols={1}>
        <Field label="Full Name" value={c.name} />
        <Field label="Email" value={c.email} />
        <Field label="Phone" value={c.phone} />
        <Field label="Applied On" value={fmtDate(c.created_at)} />
        <Field label="Source" value={c.source} muted />
      </FieldGrid>
    </Card>
    <div className="space-y-5">
      <Card title="Location & Relocation" icon={MapPin}>
        <FieldGrid>
          <Field label="Current Location" value={c.current_location} />
          <Field label="Preferred Location" value={c.preferred_location} />
          <Field label="Willing to Relocate" value={fmtTri(c.willing_to_relocate)} muted={c.willing_to_relocate == null} full />
        </FieldGrid>
      </Card>
      <Card title="Availability" icon={Clock}>
        <FieldGrid>
          <Field label="Notice Period" value={fmtNotice(c.notice_period_days)} />
          <Field label="Notice Negotiable" value={fmtTri(c.notice_negotiable)} muted={c.notice_negotiable == null} />
        </FieldGrid>
      </Card>
      {c.notes && (
        <Card title="Application Notes">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700" data-testid="candidate-notes">{c.notes}</p>
        </Card>
      )}
    </div>
  </div>
);

export const EducationTab = ({ candidate: c, canEdit, onEdit }) => {
  const qLabel = QUALIFICATIONS.find(([k]) => k === c.highest_qualification)?.[1];
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="tab-panel-education">
      <Card title="Education" icon={GraduationCap} action={<EditAction canEdit={canEdit} onEdit={onEdit} testId="education-edit-btn" />} data-testid="drawer-education-section">
        <FieldGrid cols={1}>
          <Field label="Highest Qualification" value={qLabel || (c.qualification_detail ? 'Other' : null)} />
          <Field label="Qualification Detail" value={c.qualification_detail} />
          <Field label="Summary" value={fmtQualification(c.highest_qualification, c.qualification_detail)} muted />
        </FieldGrid>
      </Card>
      <Card title="Work Experience" icon={ShieldCheck}>
        <FieldGrid cols={1}>
          <Field label="Relevant Experience" value={fmtExperience(c.relevant_experience)} />
          <Field label="Total Experience" value={fmtExperience(c.total_experience)} />
        </FieldGrid>
      </Card>
    </div>
  );
};

export const CompensationTab = ({ candidate: c, canEdit, onEdit }) => (
  <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="tab-panel-compensation">
    <Card title="Compensation" icon={BadgeDollarSign} action={<EditAction canEdit={canEdit} onEdit={onEdit} testId="compensation-edit-btn" />} data-testid="drawer-ctc-block">
      <FieldGrid cols={1}>
        <Field label="Current CTC (annual)" value={fmtCtc(c.current_ctc)} />
        <Field label="Expected CTC (annual)" value={fmtCtc(c.expected_ctc)} />
        <Field label="CTC Negotiable" value={fmtTri(c.ctc_negotiable)} muted={c.ctc_negotiable == null} />
      </FieldGrid>
    </Card>
    <Card title="Visibility" icon={ShieldCheck} className="self-start">
      <p className="text-xs leading-relaxed text-slate-500">Compensation details are restricted to Admin and HR Manager. Other recruitment roles never receive these values from the server.</p>
    </Card>
  </div>
);
